import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { Bq6CargoGearM, initialBq6CargoGear, mapSqlToBq6CargoGearForm } from "../models/bq6-cargo-gear.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF6CargoGearAtom } from "../state/baltic-form.atoms";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq6CargoGear: React.FC<Props> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF6CargoGearAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq6CargoGearM>(initialBq6CargoGear);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_CargoGear`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq6CargoGearForm(_formSql);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="text-green">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Cargo Gear (only if applicable)</h1>

        <form onSubmit={handleSubmit}>
          {/* Geared make and type */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">If geared, state make and type</label>
              <input
                disabled={readonly}
                type="text"
                name="gearMakeType"
                value={form.gearMakeType}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* No Derricks */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">How many derricks are fitted?</label>
              <input
                disabled={readonly}
                type="number"
                name="noOfDerricksFitted"
                value={form.noOfDerricksFitted}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            {/* Derricks SWL */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">What is the safe working load (SWL) of the derricks?:</label>
              <InputCell
                disabled={readonly}
                name="derricksSafeWorkingLoad"
                value={form.derricksSafeWorkingLoad}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* No of cranes */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">If cranes are fitted, how many?:</label>
              <input
                disabled={readonly}
                type="number"
                name="noOfCranesFitted"
                value={form.noOfCranesFitted}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            {/* Cranes SWL */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">What is the safe working load (SWL) of the cranes?:</label>
              <InputCell
                disabled={readonly}
                name="cranesSafeWorkingLoad"
                value={form.cranesSafeWorkingLoad}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Location of derricks/cranes */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-bold">Location of derricks/cranes (port/center/starboard):</label>
              <textarea
                disabled={readonly}
                name="cranesLocation"
                value={form.cranesLocation}
                onChange={handleChange}
                rows={2}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Max outreach outboard of ship’s side */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                What is maximum outreach of vessel's cranes or derricks outboard of the ship's side?:
              </label>
              <InputCell
                disabled={readonly}
                name="maxOutreachShipSide"
                value={form.maxOutreachShipSide}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("maxOutreachShipSideComments")}
                >
                  Comments
                </button>
              </label>
              {(form.maxOutreachShipSideComments || isCommentsVisible("maxOutreachShipSideComments")) && (
                <textarea
                  disabled={readonly}
                  name="maxOutreachShipSideComments"
                  value={form.maxOutreachShipSideComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Max outreach beyond rail w/ max lift */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Max outreach of gear beyond ship's rail with max cargo lift on hook:
              </label>
              <InputCell
                disabled={readonly}
                name="maxOutreachBeyondRail"
                value={form.maxOutreachBeyondRail}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("maxOutreachBeyondRailComments")}
                >
                  Comments
                </button>
              </label>
              {(form.maxOutreachBeyondRailComments || isCommentsVisible("maxOutreachBeyondRailComments")) && (
                <textarea
                  disabled={readonly}
                  name="maxOutreachBeyondRailComments"
                  value={form.maxOutreachBeyondRailComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Min clearance dist crane hook to top hatch coaming */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                If vessel has gantry cranes/horizontal slewing cranes, state minimum clearance distance crane hook to
                top of hatch coaming:
              </label>
              <InputCell
                disabled={readonly}
                name="hookMinClearanceDistance"
                value={form.hookMinClearanceDistance}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>
          </div>

          {/* Time needed for full cycle w/ max lift on hook (Seconds) */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Time needed for full cycle with maximum cargo lift on hook
              </label>
              <InputCell
                disabled={readonly}
                name="hookFullCycleTime"
                value={form.hookFullCycleTime}
                onChange={handleChange}
                trailingText="Seconds"
                inputType="number"
              />
            </div>

            {/* Hoisting time of gear (Metres/Minute) */}
            <div className="col-span-1">
              <label className="block text-sm font-bold"> Hoisting time of gear:</label>
              <InputCell
                disabled={readonly}
                name="hoistingTime"
                value={form.hoistingTime}
                onChange={handleChange}
                trailingText="Meters/Minute"
                inputType="number"
              />
            </div>
          </div>

          {/* Luffing time of gear: */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Luffing time of gear:</label>
              <InputCell
                disabled={readonly}
                name="luffingTime"
                value={form.luffingTime}
                onChange={handleChange}
                trailingText="Seconds"
                inputType="number"
              />
            </div>

            {/* Slewing time of gear: */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">Slewing time of gear:</label>
              <InputCell
                disabled={readonly}
                name="slewingTime"
                value={form.slewingTime}
                onChange={handleChange}
                trailingText="rpm"
                inputType="number"
              />
            </div>
          </div>

          {/* Is gear combinable for heavy lift?: */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Is gear combinable for heavy lift?</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="gearCombinableHeavyLift"
                    value="yes"
                    checked={form.gearCombinableHeavyLift === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="gearCombinableHeavyLift"
                    value="no"
                    checked={form.gearCombinableHeavyLift === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="gearCombinableHeavyLift"
                    value="n/a"
                    checked={form.gearCombinableHeavyLift === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("gearCombinableHeavyLiftComments")}
                >
                  Comments
                </button>
              </label>
              {(form.gearCombinableHeavyLiftComments || isCommentsVisible("gearCombinableHeavyLiftComments")) && (
                <textarea
                  disabled={readonly}
                  name="gearCombinableHeavyLiftComments"
                  value={form.gearCombinableHeavyLiftComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Are winches electro-hydraulic? */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">Are winches electro-hydraulic?</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="winchesElectroHydraulic"
                    value="yes"
                    checked={form.winchesElectroHydraulic === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="winchesElectroHydraulic"
                    value="no"
                    checked={form.winchesElectroHydraulic === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="winchesElectroHydraulic"
                    value="n/a"
                    checked={form.winchesElectroHydraulic === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("winchesElectroHydraulicComments")}
                >
                  Comments
                </button>
              </label>
              {(form.winchesElectroHydraulicComments || isCommentsVisible("winchesElectroHydraulicComments")) && (
                <textarea
                  disabled={readonly}
                  name="winchesElectroHydraulicComments"
                  value={form.winchesElectroHydraulicComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          {/* If vessel has grabs on board */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">If vessel has grabs on board - state the following</h2>

          {/* Type of grabs */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-xs font-bold">State type of grabs on board vessel (if applicable):</label>
              <input
                disabled={readonly}
                type="text"
                name="grabTypeIfFitted"
                value={form.grabTypeIfFitted}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            {/* Number of grabs on board */}
            <div className="col-span-1">
              <label className="block text-xs font-bold">Number of grabs on board vessel (if fitted):</label>
              <input
                disabled={readonly}
                type="number"
                name="numberOfGrabsOnBoard"
                value={form.numberOfGrabsOnBoard}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Capacity of grabs (Cu. Metres) */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-xs font-bold">
                State capacity of grabs on board vessel (if applicable):
              </label>
              <InputCell
                disabled={readonly}
                name="grabCapacity"
                value={form.grabCapacity}
                onChange={handleChange}
                trailingText="Cu. Metres"
                inputType="number"
              />
            </div>

            {/* Power source - Amps */}
            <div className="col-span-1">
              <label className="block text-xs font-bold">Power source - Amps</label>
              <InputCell
                disabled={readonly}
                name="grabsPowerAmps"
                value={form.grabsPowerAmps}
                onChange={handleChange}
                trailingText="Amps"
                inputType="number"
              />
            </div>

            <div>
              <label className="block text-xs font-bold">Power source - Volts</label>
              <InputCell
                disabled={readonly}
                name="grabsPowerVolts"
                value={form.grabsPowerVolts}
                onChange={handleChange}
                trailingText="Volts"
                inputType="number"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            {/* Does vessel have enough power to run 4 cranes & 4 shore grabs? */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Does vessel have enough power to run 4 cranes and 4 shore grabs (if applicable). If not please state how
                many cranes/grabs?:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="enoughPowerForCranesGrabs"
                value={form.enoughPowerForCranesGrabs}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
                placeholder="If no, state how many can be run"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              {/* Sufficient lights at hatches for night? */}
              <label className="block text-sm font-bold">
                Is vessel fitted with sufficient lights at each hatch for night work?:
              </label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselFittedLights"
                    value="yes"
                    checked={form.vesselFittedLights === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselFittedLights"
                    value="no"
                    checked={form.vesselFittedLights === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselFittedLights"
                    value="n/a"
                    checked={form.vesselFittedLights === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("vesselFittedLightsComments")}
                >
                  Comments
                </button>
              </label>
              {(form.vesselFittedLightsComments || isCommentsVisible("vesselFittedLightsComments")) && (
                <textarea
                  disabled={readonly}
                  name="vesselFittedLightsComments"
                  value={form.vesselFittedLightsComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Is vessel logs fitted? */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">Is vessel logs fitted?</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLogsFitted"
                    value="yes"
                    checked={form.vesselLogsFitted === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLogsFitted"
                    value="no"
                    checked={form.vesselLogsFitted === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLogsFitted"
                    value="n/a"
                    checked={form.vesselLogsFitted === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
            </div>
          </div>

          {/* If vessel is logs fitted, details (number/type/height stanchions) */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                If vessel is logs fitted, state number, type and height of stanchions/sockets:
              </label>
              <textarea
                disabled={readonly}
                name="logsFittedDetails"
                value={form.logsFittedDetails}
                onChange={handleChange}
                rows={3}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Is vessel log racks fitted? */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold">Is vessel log racks fitted?</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLogRacksFitted"
                    value="yes"
                    checked={form.vesselLogRacksFitted === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLogRacksFitted"
                    value="no"
                    checked={form.vesselLogRacksFitted === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLogRacksFitted"
                    value="n/a"
                    checked={form.vesselLogRacksFitted === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("vesselLogRacksFittedComments")}
                >
                  Comments
                </button>
              </label>
              {(form.vesselLogRacksFittedComments || isCommentsVisible("vesselLogRacksFittedComments")) && (
                <textarea
                  disabled={readonly}
                  name="vesselLogRacksFittedComments"
                  value={form.vesselLogRacksFittedComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Is vessel Lakes fitted? */}
            <div>
              <label className="block text-sm font-bold">Is vessel Lakes fitted?</label>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLakesFitted"
                    value="yes"
                    checked={form.vesselLakesFitted === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselLakesFitted"
                    value="no"
                    checked={form.vesselLakesFitted === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>
          </div>

          {/* Lakes Net Registered Tonnage */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Lakes Net Registered Tonnage</label>
              <InputCell
                disabled={readonly}
                name="lakesNRT"
                value={form.lakesNRT}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>

            {/* Lakes Gross Registered Tonnage */}
            <div>
              <label className="block text-sm font-bold">Lakes Gross Registered Tonnage</label>
              <InputCell
                disabled={readonly}
                name="lakesGRT"
                value={form.lakesGRT}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Timber Loadline (if applicable) */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Timber Loadline (if applicable)</h2>

          {/* Summer */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Summer Timber Deadweight</label>
              <InputCell
                disabled={readonly}
                name="summerTimberDW"
                value={form.summerTimberDW}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("summerTimberDWComments")}
                >
                  Comments
                </button>
              </label>
              {(form.summerTimberDWComments || isCommentsVisible("summerTimberDWComments")) && (
                <textarea
                  disabled={readonly}
                  name="summerTimberDWComments"
                  value={form.summerTimberDWComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-bold">Summer Timber Draft</label>
              <InputCell
                disabled={readonly}
                name="summerTimberDraft"
                value={form.summerTimberDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("summerTimberDraftComments")}
                >
                  Comments
                </button>
              </label>
              {(form.summerTimberDraftComments || isCommentsVisible("summerTimberDraftComments")) && (
                <textarea
                  disabled={readonly}
                  name="summerTimberDraftComments"
                  value={form.summerTimberDraftComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">TPC Immersion at Summer Timber Draft (Freeboard)</label>
              <InputCell
                disabled={readonly}
                name="tpcSummerTimberDraft"
                value={form.tpcSummerTimberDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Winter */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Winter Timber Deadweight</label>
              <InputCell
                disabled={readonly}
                name="winterTimberDW"
                value={form.winterTimberDW}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Winter Timber Draft</label>
              <InputCell
                disabled={readonly}
                name="winterTimberDraft"
                value={form.winterTimberDraft}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">TPC Immersion at Winter Timber Draft (Freeboard)</label>
              <InputCell
                disabled={readonly}
                name="tpcWinterTimberDraft"
                value={form.tpcWinterTimberDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Winter North Atlantic */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Winter North Atlantic Deadweight</label>
              <InputCell
                disabled={readonly}
                name="winterNorthAtlanticDW"
                value={form.winterNorthAtlanticDW}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Winter North Atlantic Draft</label>
              <InputCell
                disabled={readonly}
                name="winterNorthAtlanticDraft"
                value={form.winterNorthAtlanticDraft}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">TPC Immersion at Winter N. Atlantic Draft (Freeboard)</label>
              <InputCell
                disabled={readonly}
                name="tpcWinterNorthAtlanticDraft"
                value={form.tpcWinterNorthAtlanticDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Fresh Water */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Fresh Water Timber Deadweight</label>
              <InputCell
                disabled={readonly}
                name="freshWaterTimberDW"
                value={form.freshWaterTimberDW}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Fresh Water Timber Draft</label>
              <InputCell
                disabled={readonly}
                name="freshWaterTimberDraft"
                value={form.freshWaterTimberDraft}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">TPC Immersion at Fresh Water Timber Draft (Freeboard)</label>
              <InputCell
                disabled={readonly}
                name="tpcFreshWaterTimberDraft"
                value={form.tpcFreshWaterTimberDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Tropical */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Tropical Timber Deadweight</label>
              <InputCell
                disabled={readonly}
                name="tropicalTimberDW"
                value={form.tropicalTimberDW}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Tropical Timber Draft</label>
              <InputCell
                disabled={readonly}
                name="tropicalTimberDraft"
                value={form.tropicalTimberDraft}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">TPC Immersion at Tropical Timber Draft (Freeboard)</label>
              <InputCell
                disabled={readonly}
                name="tpcTropicalTimberDraft"
                value={form.tpcTropicalTimberDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          {/* Tropical Fresh Water */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Tropical Fresh Water Deadweight</label>
              <InputCell
                disabled={readonly}
                name="tropicalFreshWaterDW"
                value={form.tropicalFreshWaterDW}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Tropical Fresh Water Draft</label>
              <InputCell
                disabled={readonly}
                name="tropicalFreshWaterDraft"
                value={form.tropicalFreshWaterDraft}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">TPC Immersion at Tropical Fresh Water Draft (Freeboard)</label>
              <InputCell
                disabled={readonly}
                name="tpcTropicalFreshWaterDraft"
                value={form.tpcTropicalFreshWaterDraft}
                onChange={handleChange}
                trailingText="Metric Tonnes"
                inputType="number"
              />
            </div>
          </div>

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
